import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Package, PlusCircle } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { Logo } from '../components/Logo';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ORDER_TIERS } from '../lib/tireSizes';
import { CLIENT_DASHBOARD } from '../constants/testIds';
import { getErrorMessage } from '../lib/errors';
import { clearClientToken } from '../lib/authTokens';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const emptyForm = { name: '', phone: '', business: '', request: '', city: '', state: '', country: '' };

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState(1600);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/client/me`, { withCredentials: true }),
      axios.get(`${API}/client/orders`, { withCredentials: true }),
    ])
      .then(([meRes, ordersRes]) => {
        setMe(meRes.data);
        setOrders(ordersRes.data);
      })
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await axios.post(`${API}/client/logout`, {}, { withCredentials: true });
    clearClientToken();
    navigate('/login');
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.request) {
      toast.error('Please fill in your name, phone, and what you need');
      return;
    }
    if (!form.country.trim()) {
      toast.error('Delivery country is required so we can calculate freight');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/client/orders`,
        {
          name: form.name, phone: form.phone, business_name: form.business || null, tier,
          request_details: form.request || null, wants_pdf: false,
          delivery_city: form.city || null, delivery_state: form.state || null, delivery_country: form.country,
        },
        { withCredentials: true }
      );
      setOrders((prev) => [res.data, ...prev]);
      setForm(emptyForm);
      setTier(1600);
      toast.success(`Request ${res.data.order_number} sent — we'll be in touch shortly!`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text font-body" data-testid={CLIENT_DASHBOARD.page}>
      <header className="flex items-center justify-between px-5 sm:px-8 h-20 border-b border-brand-border/60">
        <Logo />
        {me && <p className="hidden sm:block font-mono text-xs text-brand-muted">{me.email}</p>}
        <button
          type="button"
          data-testid={CLIENT_DASHBOARD.logoutButton}
          onClick={handleLogout}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-brand-muted border border-brand-border rounded-full px-4 py-2 hover:border-brand-orange hover:text-brand-orange transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" />
          Logout
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display font-extrabold uppercase text-3xl text-brand-text">My Account</h1>
        <p className="mt-2 text-brand-muted text-sm">Submit new bulk requests and track your order status.</p>

        {loading ? (
          <p className="mt-10 font-mono text-sm text-brand-muted">Loading…</p>
        ) : (
          <Tabs defaultValue="new-order" className="mt-8">
            <TabsList className="bg-brand-surface2 border border-brand-border p-1 rounded-lg h-auto">
              <TabsTrigger value="new-order" data-testid={CLIENT_DASHBOARD.tabNewOrder} className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <PlusCircle className="w-3.5 h-3.5" />
                New Order
              </TabsTrigger>
              <TabsTrigger value="my-orders" data-testid={CLIENT_DASHBOARD.tabMyOrders} className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5" />
                My Orders ({orders.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="new-order" className="mt-6">
              <form onSubmit={handleSubmitOrder} className="rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-8 space-y-6">
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Which Package Interests You?</Label>
                  <div className="mt-2 grid grid-cols-3 gap-3">
                    {ORDER_TIERS.map((t) => (
                      <button
                        key={t.quantity}
                        type="button"
                        onClick={() => setTier(t.quantity)}
                        className={`rounded-xl border px-3 py-3 text-center transition-colors ${
                          tier === t.quantity ? 'border-brand-orange bg-brand-orange/10' : 'border-brand-border hover:border-brand-amber'
                        }`}
                      >
                        <p className="font-display font-bold text-brand-text text-lg">{t.quantity}</p>
                        <p className="text-brand-muted text-[11px] font-mono uppercase">{t.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Full Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Business Name</Label>
                    <Input value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Phone *</Label>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                </div>
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">What Are You Looking For? *</Label>
                  <Textarea value={form.request} onChange={(e) => setForm({ ...form, request: e.target.value })} rows={3} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Delivery City</Label>
                    <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Delivery State</Label>
                    <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                  <div>
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Delivery Country *</Label>
                    <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : 'Submit Request'}
                </button>
              </form>
            </TabsContent>

            <TabsContent value="my-orders" className="mt-6">
              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid={CLIENT_DASHBOARD.ordersTable}>
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Order #', 'Tier', 'What You Need', 'Destination', 'Status', 'Date'].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={6} className="text-center text-brand-muted py-8">No orders yet — submit your first request above.</TableCell></TableRow>
                    )}
                    {orders.map((o) => (
                      <TableRow key={o.order_number} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="font-mono text-brand-amber text-xs">{o.order_number}</TableCell>
                        <TableCell className="text-brand-text text-sm">{o.tier}</TableCell>
                        <TableCell className="text-brand-muted text-xs max-w-xs">{o.request_details || '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{[o.delivery_city, o.delivery_state, o.delivery_country].filter(Boolean).join(', ') || '—'}</TableCell>
                        <TableCell className="text-brand-amber text-xs uppercase">{o.status}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(o.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
